(() => {
  'use strict';
  const header = document.getElementById('library-header');
  if (!header) return;
  const previous = window.VPHeaderMotion;
  if (previous && previous.header === header) return;
  if (previous) previous.destroy();
  const button = header.querySelector('.header-motion');
  if (!button) return;
  const label = button.querySelector('.header-motion-label');
  const symbol = button.querySelector('.header-motion-symbol');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(pointer: fine)');
  const compact = !document.body.classList.contains('home-page');
  const listeners = [];
  const stickerMotion = () => [...header.querySelectorAll('.library-sticker')].map(element => ({
    element,
    x: Number(element.dataset.driftX) || 0,
    y: Number(element.dataset.driftY) || 0,
    turn: Number(element.dataset.turn) || 0
  }));
  let paused = false;
  let frame = 0;
  let lastTime = 0;
  let progress = 0;
  let targetProgress = 0;
  let pointerX = 0;
  let pointerY = 0;
  let targetX = 0;
  let targetY = 0;
  const clamp = value => Math.max(0, Math.min(1, value));
  const enabled = () => !paused && !reduced.matches;

  function on(target, event, listener, options) {
    target.addEventListener(event, listener, options);
    listeners.push(() => target.removeEventListener(event, listener, options));
  }

  function readPreference() {
    try { paused = sessionStorage.getItem('vp_header_motion') === 'paused'; } catch (_) {}
  }

  function stop() {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    lastTime = 0;
  }

  function visible() {
    const rect = header.getBoundingClientRect();
    const active = !document.hidden && rect.bottom > -120 && rect.top < innerHeight + 35;
    const value = String(active);
    if (header.dataset.visible !== value) header.dataset.visible = value;
    return active;
  }

  function draw() {
    const scale = (innerWidth <= 720 ? .32 : 1) * (compact ? .45 : 1);
    stickerMotion().forEach(({ element, x, y, turn }, index) => {
      const depth = index % 3 + 1;
      element.style.setProperty('--shift-x', `${(progress * x * scale + pointerX * depth * 4).toFixed(2)}px`);
      element.style.setProperty('--shift-y', `${(progress * y * scale + pointerY * depth * 3).toFixed(2)}px`);
      element.style.setProperty('--turn', `${(progress * turn * (innerWidth <= 720 ? .65 : 1) * (compact ? .6 : 1)).toFixed(2)}deg`);
    });
    header.style.setProperty('--tilt-x', `${(-pointerY * 8).toFixed(2)}deg`);
    header.style.setProperty('--tilt-y', `${(pointerX * 12).toFixed(2)}deg`);
    header.style.setProperty('--spark-turn', `${(progress * 140 + pointerX * 12).toFixed(2)}deg`);
    header.style.setProperty('--wordmark-x', `${(pointerX * 6).toFixed(2)}px`);
    header.style.setProperty('--wordmark-y', `${(-progress * 26 * scale + pointerY * 2).toFixed(2)}px`);
    header.style.setProperty('--wordmark-turn', `${(progress * 4 + pointerX * .8).toFixed(2)}deg`);
    header.dataset.progress = progress.toFixed(3);
  }

  function tick(now) {
    frame = 0;
    if (!enabled() || !visible()) { lastTime = 0; return; }
    const elapsed = lastTime ? Math.min((now - lastTime) / 1000, .05) : 1 / 60;
    lastTime = now;
    const easing = 1 - Math.exp(-elapsed * 12);
    progress += (targetProgress - progress) * easing;
    pointerX += (targetX - pointerX) * easing;
    pointerY += (targetY - pointerY) * easing;
    draw();
    if (Math.abs(targetProgress - progress) + Math.abs(targetX - pointerX) + Math.abs(targetY - pointerY) > .001) {
      frame = requestAnimationFrame(tick);
    } else lastTime = 0;
  }

  function requestFrame() {
    if (enabled() && visible() && !frame) frame = requestAnimationFrame(tick);
  }

  function measure() {
    targetProgress = clamp(scrollY / Math.max(header.offsetHeight, 1));
    if (!visible()) { stop(); return; }
    requestFrame();
  }

  function syncMotion() {
    stop();
    const active = enabled();
    button.hidden = false;
    button.disabled = reduced.matches;
    button.setAttribute('aria-pressed', String(!active));
    button.setAttribute('aria-label', reduced.matches ? 'Header motion disabled by your reduced-motion setting' : active ? 'Pause header animation' : 'Enable header animation');
    label.textContent = reduced.matches ? 'Reduced motion' : active ? 'Pause motion' : 'Motion paused';
    symbol.textContent = active ? 'Ⅱ' : '▶';
    header.dataset.motion = reduced.matches ? 'reduced' : paused ? 'paused' : 'on';
    if (!active) {
      progress = pointerX = pointerY = targetX = targetY = 0;
      draw();
    } else measure();
  }

  function suspend() {
    header.dataset.visible = 'false';
    stop();
  }

  on(button, 'click', () => {
    if (reduced.matches) return;
    paused = !paused;
    try { sessionStorage.setItem('vp_header_motion', paused ? 'paused' : 'on'); } catch (_) {}
    syncMotion();
  });
  on(header, 'pointermove', event => {
    if (!enabled() || !finePointer.matches || event.pointerType === 'touch') return;
    const rect = header.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    targetX = clamp((event.clientX - rect.left) / rect.width) * 2 - 1;
    targetY = clamp((event.clientY - rect.top) / rect.height) * 2 - 1;
    requestFrame();
  }, { passive: true });
  const resetPointer = () => { targetX = targetY = 0; requestFrame(); };
  on(header, 'pointerleave', resetPointer);
  on(finePointer, 'change', resetPointer);
  on(window, 'scroll', measure, { passive: true });
  on(window, 'resize', measure, { passive: true });
  // Back/forward cache restores the old page's in-memory pause value.
  on(window, 'pageshow', () => { readPreference(); syncMotion(); });
  on(window, 'pagehide', suspend);
  on(window, 'storage', event => {
    if (event.key === 'vp_header_motion' || event.key === null) { readPreference(); syncMotion(); }
  });
  on(document, 'visibilitychange', () => {
    if (document.hidden) suspend();
    else { readPreference(); syncMotion(); }
  });
  on(reduced, 'change', syncMotion);

  // Peel and move (Josh 10/17: "free to move about for any user and just reset anytime you refresh"): any pointer
  // (mouse, pen or finger) can pick up any sticker and drop it anywhere. Nothing is saved, so a refresh resets them.
  // Home (Josh 10/17): they also stay inside the header window and bounce off its walls and each other (stickerWorld below).
  // Inner pages and arrange mode (?arrange=1, where Josh places them by hand) keep the plain drag.
  let dragTop = 20;
  let drag = null;
  const world = compact ? null : stickerWorld();
  const arranging = () => { try { return sessionStorage.getItem('vp_arrange') === '1'; } catch (_) { return false; } };
  on(header, 'pointerdown', event => {
    const sticker = event.target.closest?.('.library-sticker');
    if (!sticker || !header.contains(sticker) || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault();
    if (world && !arranging() && world.grab(sticker, event)) return;
    drag = {
      sticker,
      id: event.pointerId,
      startX: event.clientX - (parseFloat(sticker.style.getPropertyValue('--drag-x')) || 0),
      startY: event.clientY - (parseFloat(sticker.style.getPropertyValue('--drag-y')) || 0)
    };
    sticker.style.zIndex = ++dragTop;
    sticker.classList.add('is-dragging');
    sticker.setPointerCapture?.(event.pointerId);
  });
  on(header, 'pointermove', event => {
    world?.move(event);
    if (!drag || event.pointerId !== drag.id) return;
    drag.sticker.style.setProperty('--drag-x', (event.clientX - drag.startX).toFixed(1) + 'px');
    drag.sticker.style.setProperty('--drag-y', (event.clientY - drag.startY).toFixed(1) + 'px');
  });
  const drop = event => {
    world?.drop(event);
    if (!drag || event.pointerId !== drag.id) return;
    drag.sticker.classList.remove('is-dragging');
    drag = null;
  };
  on(header, 'pointerup', drop);
  on(header, 'pointercancel', drop);

  // The home header window, from the top of the band to the trust-row line, edge to edge. Every sticker stays inside it
  // and bounces off its walls and off the others: a throw keeps sliding, and one dragged hard into an edge pops out of
  // the hand and rebounds into the rest. Stickers meet each other as capsules (a round-ended bar along the long side)
  // and the walls with their rotated box, so no art leaves the window. Pairs that already sit closer than their
  // capsules allow in the approved picture keep that closeness until they part, so a grab never makes a neighbour jump.
  // Reduced motion: no throws, bounces or spin; the held sticker only nudges the others aside.
  function stickerWorld() {
    const WALL_BOUNCE = .72, BOUNCE = .82, GRIP = .18, GLIDE = 2, SPIN_DRAG = 2.4, SPIN_HEAVY = 1.6;
    const KNOCK = 650, MAX_SPEED = 3200, MAX_SPIN = 7, DRAG_SCALE = 1.07;
    let bodies = [], room = null, held = null, raf = 0, last = 0, settle = 0;
    const slack = new Map();
    const still = () => reduced.matches;
    const css = (el, name) => parseFloat(el.style.getPropertyValue(name)) || 0;

    function local(event) {
      const rect = header.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top, t: event.timeStamp };
    }

    // Layout reads, batched before any write. The pointer parallax (draw above) can slide a sticker up to depth x 4 px
    // sideways and depth x 3 px up or down, and the idle float with its tilt swells the art up to ~8 px past its box,
    // so each sticker keeps that much off the walls.
    function readRoom() {
      const float = header.dataset.motion === 'on' ? 10 : 2;
      const rect = header.getBoundingClientRect();
      const trust = document.querySelector('.home-page .hero-trust');
      const floor = trust?.getClientRects().length ? trust.getBoundingClientRect().top - rect.top : rect.height;
      room = { x1: header.clientWidth, y1: Math.max(rect.height, floor) };
      bodies.forEach(b => {
        b.left = b.el.offsetLeft; b.top = b.el.offsetTop; b.bw = b.el.offsetWidth; b.bh = b.el.offsetHeight;
        b.mx = (finePointer.matches ? b.depth * 4 : 0) + float; b.my = (finePointer.matches ? b.depth * 3 : 0) + float;
      });
    }

    // Rebuilt from the page each time the world wakes, so it follows whatever home-collage.js placed and shows.
    function collect() {
      bodies = [...header.querySelectorAll('.library-sticker')].map((el, index) => ({ el, depth: index % 3 + 1 }))
        .filter(b => b.el.offsetParent);
      readRoom();
      bodies.forEach(b => {
        b.angle = (parseFloat(getComputedStyle(b.el).getPropertyValue('--angle')) || 0) * Math.PI / 180;
        b.spin = css(b.el, '--spin') * Math.PI / 180;
        const turn = b.angle + b.spin, lift = .05 * b.bh;
        // Centre of the art: the sticker turns about 50% 55% of its box, so the centre swings a little with the angle.
        b.x = b.left + css(b.el, '--drag-x') + lift * Math.sin(turn);
        b.y = b.top + css(b.el, '--drag-y') + lift * (1 - Math.cos(turn));
        b.vx = b.vy = b.av = 0;
        b.live = false;
      });
      shapes();
      slack.clear();
      eachPair((a, b, key, depth) => { if (depth > 0) slack.set(key, depth); }, true);
    }

    function shapes() {
      bodies.forEach(b => {
        const turn = b.angle + b.spin, long = Math.max(b.bw, b.bh), short = Math.min(b.bw, b.bh);
        const axis = b.bw >= b.bh ? turn : turn + Math.PI / 2;
        b.r = short * .46;
        b.hs = Math.max(0, long / 2 - b.r);
        b.ux = Math.cos(axis) * b.hs; b.uy = Math.sin(axis) * b.hs;
        const mass = b.bw * b.bh / 1e4;
        b.invM = held?.b === b ? 0 : 1 / mass;
        b.invI = held?.b === b || still() ? 0 : 12 / (mass * (b.bw * b.bw + b.bh * b.bh) * SPIN_HEAVY);
      });
    }

    // Half the rotated box's width and height, plus the parallax margin.
    function reach(b, grow = 1) {
      const turn = b.angle + b.spin, c = Math.abs(Math.cos(turn)), s = Math.abs(Math.sin(turn));
      return [(c * b.bw + s * b.bh) / 2 * grow + b.mx, (s * b.bw + c * b.bh) / 2 * grow + b.my];
    }

    // Closest points between two capsule spines (Ericson, Real-Time Collision Detection 5.1.9).
    function closest(a, b) {
      const p1x = a.x - a.ux, p1y = a.y - a.uy, p2x = b.x - b.ux, p2y = b.y - b.uy;
      const d1x = 2 * a.ux, d1y = 2 * a.uy, d2x = 2 * b.ux, d2y = 2 * b.uy, rx = p1x - p2x, ry = p1y - p2y;
      const aa = d1x * d1x + d1y * d1y, e = d2x * d2x + d2y * d2y, f = d2x * rx + d2y * ry;
      let s = 0, t = 0;
      if (aa > 1e-6 || e > 1e-6) {
        if (aa <= 1e-6) t = clamp(f / e);
        else {
          const c = d1x * rx + d1y * ry;
          if (e <= 1e-6) s = clamp(-c / aa);
          else {
            const bb = d1x * d2x + d1y * d2y, denom = aa * e - bb * bb;
            s = denom > 1e-6 ? clamp((bb * f - c * e) / denom) : 0;
            t = (bb * s + f) / e;
            if (t < 0) { t = 0; s = clamp(-c / aa); } else if (t > 1) { t = 1; s = clamp((bb - c) / aa); }
          }
        }
      }
      return [p1x + d1x * s, p1y + d1y * s, p2x + d2x * t, p2y + d2y * t];
    }

    // depth = how far two capsules overlap (<= 0 = apart). Pairs of two resting stickers are skipped unless `all`.
    function eachPair(visit, all) {
      for (let i = 0; i < bodies.length; i++) for (let j = i + 1; j < bodies.length; j++) {
        const a = bodies[i], b = bodies[j];
        if (!all && !a.live && !b.live) continue;
        const room2 = a.r + b.r + a.hs + b.hs;
        if (Math.abs(a.x - b.x) > room2 || Math.abs(a.y - b.y) > room2) continue;
        const [ax, ay, bx, by] = closest(a, b), gap = Math.hypot(bx - ax, by - ay);
        visit(a, b, i * 256 + j, a.r + b.r - gap, ax, ay, bx, by, gap);
      }
    }

    function push(b, jx, jy, rx, ry) {
      b.vx += jx * b.invM; b.vy += jy * b.invM; b.av += (rx * jy - ry * jx) * b.invI;
    }

    function collide() {
      eachPair((a, b, key, depth, ax, ay, bx, by, gap) => {
        let allowed = slack.get(key) || 0;
        if (allowed) {
          if (depth < allowed) { allowed = Math.max(0, depth); allowed ? slack.set(key, allowed) : slack.delete(key); }
          depth -= allowed;
        }
        if (depth <= 0) return;
        const im = a.invM + b.invM;
        if (!im) return;
        let nx = bx - ax, ny = by - ay;
        if (gap < 1e-3) { nx = b.x - a.x; ny = b.y - a.y; }
        const length = Math.hypot(nx, ny) || 1;
        nx /= length; ny /= length;
        a.live = b.live = true;
        const shove = Math.max(0, depth - .3) * .8 / im;
        a.x -= nx * shove * a.invM; a.y -= ny * shove * a.invM;
        b.x += nx * shove * b.invM; b.y += ny * shove * b.invM;
        if (still()) return;
        const cx = (ax + nx * a.r + bx - nx * b.r) / 2, cy = (ay + ny * a.r + by - ny * b.r) / 2;
        const rax = cx - a.x, ray = cy - a.y, rbx = cx - b.x, rby = cy - b.y;
        const rvx = b.vx - b.av * rby - (a.vx - a.av * ray), rvy = b.vy + b.av * rbx - (a.vy + a.av * rax);
        const vn = rvx * nx + rvy * ny;
        if (vn >= 0) return;
        const ran = rax * ny - ray * nx, rbn = rbx * ny - rby * nx;
        const j = -(1 + BOUNCE) * vn / (im + ran * ran * a.invI + rbn * rbn * b.invI);
        push(a, -j * nx, -j * ny, rax, ray); push(b, j * nx, j * ny, rbx, rby);
        // A little grip, so glancing hits set stickers turning.
        const tx = -ny, ty = nx, vt = rvx * tx + rvy * ty;
        const rat = rax * ty - ray * tx, rbt = rbx * ty - rby * tx;
        const jt = Math.max(-GRIP * j, Math.min(GRIP * j, -vt / (im + rat * rat * a.invI + rbt * rbt * b.invI)));
        push(a, -jt * tx, -jt * ty, rax, ray); push(b, jt * tx, jt * ty, rbx, rby);
      });
    }

    function walls(b) {
      const [hx, hy] = reach(b);
      const x0 = hx, x1 = Math.max(hx, room.x1 - hx), y0 = hy, y1 = Math.max(hy, room.y1 - hy);
      if (b.x < x0) { b.x = x0; if (b.vx < 0) { b.vx *= -WALL_BOUNCE; b.av *= .8; } }
      else if (b.x > x1) { b.x = x1; if (b.vx > 0) { b.vx *= -WALL_BOUNCE; b.av *= .8; } }
      if (b.y < y0) { b.y = y0; if (b.vy < 0) { b.vy *= -WALL_BOUNCE; b.av *= .8; } }
      else if (b.y > y1) { b.y = y1; if (b.vy > 0) { b.vy *= -WALL_BOUNCE; b.av *= .8; } }
    }

    // The pointer's speed over its last ~70 ms (0 if it had stopped before letting go).
    function pointerSpeed(now) {
      const trail = held.trail, end = trail[trail.length - 1];
      if (!end || now - end.t > 60) return [0, 0];
      const start = trail.find(p => end.t - p.t <= 70) || end, time = Math.max(16, end.t - start.t) / 1000;
      const k = Math.min(1, MAX_SPEED / (Math.hypot(end.x - start.x, end.y - start.y) / time || 1));
      return [(end.x - start.x) / time * k, (end.y - start.y) / time * k];
    }

    function letGo(now, rebound) {
      const b = held.b, [vx, vy] = still() ? [0, 0] : pointerSpeed(now);
      b.el.classList.remove('is-dragging');
      try { b.el.releasePointerCapture?.(held.id); } catch (_) {}
      held = null;
      b.vx = vx; b.vy = vy; b.av = 0;
      if (rebound) rebound(b);
      shapes();
      settle = 12;
    }

    function step(now) {
      raf = 0;
      if (!header.isConnected) return;
      const dt = last ? Math.min(.033, (now - last) / 1000) : 1 / 60;
      last = now;
      readRoom();
      shapes();
      // The held sticker follows the hand, stopped by the walls. Slammed into one, it pops loose and rebounds.
      let from = null;
      if (held) {
        const b = held.b, [hx, hy] = reach(b, DRAG_SCALE);
        let x = held.tx, y = held.ty, hitX = 0, hitY = 0;
        if (x < hx) { x = hx; hitX = 1; } else if (x > room.x1 - hx) { x = Math.max(hx, room.x1 - hx); hitX = -1; }
        if (y < hy) { y = hy; hitY = 1; } else if (y > room.y1 - hy) { y = Math.max(hy, room.y1 - hy); hitY = -1; }
        const [px, py] = pointerSpeed(now);
        const slamX = hitX && -hitX * px > KNOCK, slamY = hitY && -hitY * py > KNOCK;
        from = { b, x: b.x, y: b.y };
        b.vx += ((x - b.x) / dt - b.vx) * .6; b.vy += ((y - b.y) / dt - b.vy) * .6;
        b.tx = x; b.ty = y;
        if (!still() && (slamX || slamY)) {
          b.x = x; b.y = y; from = null;
          letGo(now, body => {
            body.vx = slamX ? -px * WALL_BOUNCE : px; body.vy = slamY ? -py * WALL_BOUNCE : py;
            body.av = (slamX ? py * hitX : -px * hitY) * .002;
          });
        }
      }
      let fastest = 0;
      bodies.forEach(b => { if (b !== from?.b) fastest = Math.max(fastest, Math.hypot(b.vx, b.vy)); });
      if (from) fastest = Math.max(fastest, Math.hypot(from.b.tx - from.x, from.b.ty - from.y) / dt);
      const steps = Math.min(8, Math.max(1, Math.ceil(fastest * dt / 8))), h = dt / steps;
      for (let k = 1; k <= steps; k++) {
        bodies.forEach(b => {
          if (b === from?.b) { b.x = from.x + (b.tx - from.x) * k / steps; b.y = from.y + (b.ty - from.y) * k / steps; return; }
          if (still()) { b.vx = b.vy = b.av = 0; }
          b.x += b.vx * h; b.y += b.vy * h; b.spin += b.av * h;
        });
        shapes();
        collide();
        collide();
        bodies.forEach(b => { if (b.live && b !== from?.b) walls(b); });
      }
      const glide = Math.exp(-GLIDE * dt), spinDrag = Math.exp(-SPIN_DRAG * dt);
      let moving = !!held;
      bodies.forEach(b => {
        if (b === held?.b) return;
        b.vx *= glide; b.vy *= glide;
        b.av = Math.max(-MAX_SPIN, Math.min(MAX_SPIN, b.av * spinDrag));
        if (Math.hypot(b.vx, b.vy) < 4 && Math.abs(b.av) < .04) b.vx = b.vy = b.av = 0;
        else moving = true;
      });
      bodies.forEach(b => {
        if (!b.live) return;
        const turn = b.angle + b.spin, lift = .05 * b.bh;
        b.el.style.setProperty('--drag-x', (b.x - b.left - lift * Math.sin(turn)).toFixed(1) + 'px');
        b.el.style.setProperty('--drag-y', (b.y - b.top - lift * (1 - Math.cos(turn))).toFixed(1) + 'px');
        b.el.style.setProperty('--spin', (b.spin * 180 / Math.PI).toFixed(2) + 'deg');
      });
      if (moving || settle-- > 0) raf = requestAnimationFrame(step);
      else last = 0;
    }

    function start() { if (!raf) { last = 0; raf = requestAnimationFrame(step); } }

    return {
      grab(sticker, event) {
        if (!raf || !bodies.some(b => b.el === sticker)) collect();
        const b = bodies.find(body => body.el === sticker);
        if (!b) return false;
        if (held) letGo(event.timeStamp);
        const p = local(event);
        held = { b, id: event.pointerId, gx: b.x - p.x, gy: b.y - p.y, tx: b.x, ty: b.y, trail: [p] };
        b.vx = b.vy = b.av = 0;
        b.live = true;
        shapes();
        sticker.style.zIndex = ++dragTop;
        sticker.classList.add('is-dragging');
        sticker.setPointerCapture?.(event.pointerId);
        start();
        return true;
      },
      move(event) {
        if (!held || event.pointerId !== held.id) return;
        const p = local(event);
        held.tx = p.x + held.gx; held.ty = p.y + held.gy;
        held.trail.push(p);
        while (held.trail.length > 2 && p.t - held.trail[0].t > 120) held.trail.shift();
      },
      drop(event) {
        if (!held || event.pointerId !== held.id) return;
        letGo(event.timeStamp);
        start();
      },
      stop() { if (raf) cancelAnimationFrame(raf); raf = 0; }
    };
  }
  window.VPHeaderMotion = {
    header,
    destroy() {
      suspend();
      world?.stop();
      listeners.forEach(remove => remove());
      button.hidden = true;
      delete window.VPHeaderMotion;
    }
  };
  header.dataset.variant = compact ? 'compact' : 'home';
  readPreference();
  syncMotion();
})();
